import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react'
import { useServerFn } from '@tanstack/react-start'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { supabase } from '@/integrations/supabase/client'
import { toast } from 'sonner'
import {
  generateInvoicePdf,
  emailInvoiceToCustomer,
  getInvoiceDownloadUrl,
} from '@/lib/invoices.functions'

export const Route = createFileRoute('/admin/invoices')({
  component: AdminInvoicesPage,
})

interface InvoiceRow {
  id: string
  invoice_number: string
  amount_cents: number
  status: string
  issued_at: string
  invoice_email_sent_at: string | null
  invoice_pdf_path: string | null
  appointment_id: string
  customer_id: string
  appointments: {
    service_label: string
    scheduled_at: string
    service_address: string
    vehicle_snapshot: string | null
    status: string
  } | null
  customers: {
    name: string
    email: string
    phone: string
    vehicle_make: string | null
    vehicle_model: string | null
    vehicle_year: number | null
  } | null
}

type StatusFilter = 'all' | 'draft' | 'sent' | 'paid' | 'void'

const fmtMoney = (cents: number) => `$${(cents / 100).toFixed(2)}`
const fmt = (ts: string | null) =>
  ts
    ? new Date(ts).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZone: 'America/New_York',
      })
    : '—'

function StatusPill({ status }: { status: string }) {
  const map: Record<string, string> = {
    draft: 'bg-muted text-muted-foreground',
    sent: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
    paid: 'bg-green-500/10 text-green-600 dark:text-green-400',
    void: 'bg-destructive/10 text-destructive',
  }
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize ${map[status] ?? 'bg-muted text-muted-foreground'}`}
    >
      {status}
    </span>
  )
}

function AdminInvoicesPage() {
  const navigate = useNavigate()
  const [checking, setChecking] = useState(true)
  const [isAdmin, setIsAdmin] = useState(false)
  const [userEmail, setUserEmail] = useState<string | null>(null)
  const [rows, setRows] = useState<InvoiceRow[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [pendingId, setPendingId] = useState<string | null>(null)
  const [pendingAction, setPendingAction] = useState<'pdf' | 'email' | 'view' | null>(null)

  const generateFn = useServerFn(generateInvoicePdf)
  const emailFn = useServerFn(emailInvoiceToCustomer)
  const downloadFn = useServerFn(getInvoiceDownloadUrl)

  useEffect(() => {
    let mounted = true
    const check = async () => {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        navigate({ to: '/admin/login' })
        return
      }
      if (!mounted) return
      setUserEmail(session.user.email ?? null)
      const { data: roleRow } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', session.user.id)
        .eq('role', 'admin')
        .maybeSingle()
      if (!mounted) return
      setIsAdmin(!!roleRow)
      setChecking(false)
    }
    check()
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      if (!session) navigate({ to: '/admin/login' })
    })
    return () => {
      mounted = false
      sub.subscription.unsubscribe()
    }
  }, [navigate])

  const loadInvoices = async () => {
    setLoading(true)
    const { data, error } = await supabase
      .from('invoices')
      .select(
        'id, invoice_number, amount_cents, status, issued_at, invoice_email_sent_at, invoice_pdf_path, appointment_id, customer_id, appointments (service_label, scheduled_at, service_address, vehicle_snapshot, status), customers (name, email, phone, vehicle_make, vehicle_model, vehicle_year)'
      )
      .order('issued_at', { ascending: false })
      .limit(500)
    if (error) {
      toast.error('Failed to load invoices')
      setRows([])
    } else {
      setRows((data as any) ?? [])
    }
    setLoading(false)
  }

  useEffect(() => {
    if (!isAdmin) return
    loadInvoices()
  }, [isAdmin])

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (statusFilter !== 'all' && r.status !== statusFilter) return false
      if (search) {
        const q = search.toLowerCase()
        const hay =
          `${r.invoice_number} ${r.customers?.name ?? ''} ${r.customers?.email ?? ''} ${r.appointments?.service_label ?? ''}`.toLowerCase()
        if (!hay.includes(q)) return false
      }
      return true
    })
  }, [rows, statusFilter, search])

  const stats = useMemo(() => {
    const total = rows.length
    const draft = rows.filter((r) => r.status === 'draft').length
    const sent = rows.filter((r) => r.status === 'sent').length
    const revenueCents = rows
      .filter((r) => r.status === 'paid')
      .reduce((s, r) => s + r.amount_cents, 0)
    return { total, draft, sent, revenueCents }
  }, [rows])

  const handleGenerate = async (id: string) => {
    setPendingId(id)
    setPendingAction('pdf')
    try {
      const res = await generateFn({ data: { invoiceId: id } })
      toast.success('Invoice PDF generated')
      if (res.url) window.open(res.url, '_blank', 'noopener')
      await loadInvoices()
    } catch (e: any) {
      toast.error(e?.message || 'Failed to generate PDF')
    } finally {
      setPendingId(null)
      setPendingAction(null)
    }
  }

  const handleEmail = async (id: string, recipient: string) => {
    if (!confirm(`Email invoice to ${recipient}?`)) return
    setPendingId(id)
    setPendingAction('email')
    try {
      await emailFn({ data: { invoiceId: id } })
      toast.success(`Invoice emailed to ${recipient}`)
      await loadInvoices()
    } catch (e: any) {
      toast.error(e?.message || 'Failed to send email')
    } finally {
      setPendingId(null)
      setPendingAction(null)
    }
  }

  const handleView = async (id: string) => {
    setPendingId(id)
    setPendingAction('view')
    try {
      const res = await downloadFn({ data: { invoiceId: id } })
      if (res.url) window.open(res.url, '_blank', 'noopener')
      else toast.error('No PDF available')
    } catch (e: any) {
      toast.error(e?.message || 'Failed to open PDF')
    } finally {
      setPendingId(null)
      setPendingAction(null)
    }
  }

  const handleStatusChange = async (id: string, next: string) => {
    const { error } = await supabase
      .from('invoices')
      .update({ status: next as any, ...(next === 'paid' ? { paid_at: new Date().toISOString() } : {}) })
      .eq('id', id)
    if (error) toast.error('Failed to update status')
    else {
      toast.success('Status updated')
      await loadInvoices()
    }
  }

  const handleSignOut = async () => {
    await supabase.auth.signOut()
    navigate({ to: '/admin/login' })
  }

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">
        Loading…
      </div>
    )
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-md text-center space-y-3">
          <h1 className="text-2xl font-bold">Not authorized</h1>
          <p className="text-sm text-muted-foreground">
            Your account ({userEmail}) doesn't have admin access.
          </p>
          <Button variant="outline" onClick={handleSignOut}>Sign out</Button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-xl font-bold">Manage Invoices</h1>
            <p className="text-xs text-muted-foreground">{userEmail}</p>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/admin">
              <Button variant="outline" size="sm">Bookings</Button>
            </Link>
            <Button variant="outline" size="sm" onClick={handleSignOut}>Sign out</Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Total invoices" value={String(stats.total)} />
          <StatCard label="Draft" value={String(stats.draft)} />
          <StatCard label="Sent" value={String(stats.sent)} />
          <StatCard label="Paid revenue" value={fmtMoney(stats.revenueCents)} accent="green" />
        </div>

        <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-end">
          <div className="flex-1">
            <Label htmlFor="search">Search</Label>
            <Input
              id="search"
              placeholder="Invoice #, customer, service…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="statusFilter">Status</Label>
            <select
              id="statusFilter"
              className="h-10 w-full md:w-44 rounded-md border bg-background px-3 text-sm"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            >
              <option value="all">All</option>
              <option value="draft">Draft</option>
              <option value="sent">Sent</option>
              <option value="paid">Paid</option>
              <option value="void">Void</option>
            </select>
          </div>
        </div>

        <div className="border rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="text-left px-4 py-3">Invoice</th>
                  <th className="text-left px-4 py-3">Customer</th>
                  <th className="text-left px-4 py-3">Vehicle / Service</th>
                  <th className="text-left px-4 py-3">Total</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3">Email sent</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                      Loading invoices…
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                      No invoices yet. They appear here automatically when you mark a booking as
                      completed.
                    </td>
                  </tr>
                ) : (
                  filtered.map((r) => {
                    const vehicle =
                      r.appointments?.vehicle_snapshot ||
                      [r.customers?.vehicle_year, r.customers?.vehicle_make, r.customers?.vehicle_model]
                        .filter(Boolean)
                        .join(' ') ||
                      '—'
                    const isBusy = pendingId === r.id
                    return (
                      <tr key={r.id} className="border-t align-top">
                        <td className="px-4 py-3">
                          <div className="font-mono text-xs">{r.invoice_number}</div>
                          <div className="text-xs text-muted-foreground">{fmt(r.issued_at)}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-medium">{r.customers?.name ?? '—'}</div>
                          <div className="text-xs text-muted-foreground">{r.customers?.email}</div>
                          <div className="text-xs text-muted-foreground">{r.customers?.phone}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-medium">{vehicle}</div>
                          <div className="text-xs text-muted-foreground">
                            {r.appointments?.service_label}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {fmt(r.appointments?.scheduled_at ?? null)}
                          </div>
                        </td>
                        <td className="px-4 py-3 font-semibold">{fmtMoney(r.amount_cents)}</td>
                        <td className="px-4 py-3">
                          <div className="space-y-1">
                            <StatusPill status={r.status} />
                            <select
                              className="block h-7 text-xs rounded border bg-background px-2"
                              value={r.status}
                              onChange={(e) => handleStatusChange(r.id, e.target.value)}
                            >
                              <option value="draft">Draft</option>
                              <option value="sent">Sent</option>
                              <option value="paid">Paid</option>
                              <option value="void">Void</option>
                            </select>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs">
                          {r.invoice_email_sent_at ? (
                            <span className="text-green-600 dark:text-green-400">
                              {fmt(r.invoice_email_sent_at)}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">Not sent</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-col gap-1.5 items-end">
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={isBusy}
                              onClick={() => handleGenerate(r.id)}
                            >
                              {isBusy && pendingAction === 'pdf' ? 'Generating…' : 'Generate PDF'}
                            </Button>
                            {r.invoice_pdf_path && (
                              <Button
                                size="sm"
                                variant="ghost"
                                disabled={isBusy}
                                onClick={() => handleView(r.id)}
                              >
                                {isBusy && pendingAction === 'view' ? 'Opening…' : 'View PDF'}
                              </Button>
                            )}
                            <Button
                              size="sm"
                              disabled={isBusy || !r.customers?.email}
                              onClick={() => handleEmail(r.id, r.customers?.email ?? '')}
                            >
                              {isBusy && pendingAction === 'email' ? 'Sending…' : 'Email Invoice'}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  )
}

function StatCard({
  label,
  value,
  accent,
}: {
  label: string
  value: string
  accent?: 'green'
}) {
  const color = accent === 'green' ? 'text-green-600 dark:text-green-400' : 'text-foreground'
  return (
    <div className="border rounded-xl p-4 bg-card">
      <p className="text-xs text-muted-foreground uppercase tracking-wide">{label}</p>
      <p className={`text-2xl font-bold mt-1 ${color}`}>{value}</p>
    </div>
  )
}
